// app/src/lib/snapping/candidates.ts
import type { SnapCandidate, Vec3 } from './types';

type Position = number[];
interface Geometry {
  type: string;
  coordinates: any;
}
export interface SnapFeature {
  id: string;
  geometry: Geometry;
}

const v3 = (p: Position): Vec3 => [p[0] ?? 0, p[1] ?? 0, p[2] ?? 0];
const mid = (a: Vec3, b: Vec3): Vec3 => [(a[0] + b[0]) / 2, (a[1] + b[1]) / 2, (a[2] + b[2]) / 2];

/** Append vertex/midpoint/edge candidates for one ordered ring/line of positions. */
function walkLine(coords: Position[], featureId: string, out: SnapCandidate[]): void {
  const pts = coords.map(v3);
  for (const p of pts) out.push({ kind: 'vertex', position: p, featureId });
  for (let i = 0; i < pts.length - 1; i++) {
    const a = pts[i];
    const b = pts[i + 1];
    out.push({ kind: 'edge', a, b, featureId });
    out.push({ kind: 'midpoint', position: mid(a, b), featureId });
  }
}

/** Flatten visible features into snap candidates. Closed rings keep their closing edge. */
export function gatherCandidates(features: SnapFeature[]): SnapCandidate[] {
  const out: SnapCandidate[] = [];
  for (const f of features) {
    const g = f.geometry;
    switch (g.type) {
      case 'Point':
        out.push({ kind: 'vertex', position: v3(g.coordinates), featureId: f.id });
        break;
      case 'MultiPoint':
        for (const p of g.coordinates as Position[])
          out.push({ kind: 'vertex', position: v3(p), featureId: f.id });
        break;
      case 'LineString':
        walkLine(g.coordinates as Position[], f.id, out);
        break;
      case 'MultiLineString':
        for (const line of g.coordinates as Position[][]) walkLine(line, f.id, out);
        break;
      case 'Polygon':
        for (const ring of g.coordinates as Position[][]) walkLine(ring, f.id, out);
        break;
      case 'MultiPolygon':
        for (const poly of g.coordinates as Position[][][])
          for (const ring of poly) walkLine(ring, f.id, out);
        break;
      default:
        break;
    }
  }
  return out;
}
